/* https://sweetalert2.github.io/ */
import DOMPurify from 'dompurify';
import Swal, { type SweetAlertOptions, type SweetAlertResult } from 'sweetalert2';

/** Classes de la charte, définies dans `styles/globals.css`. */
export const swalCustomClass = {
    popup: 'swal-custom-popup',
    title: 'swal-custom-title',
    htmlContainer: 'swal-custom-html',
    confirmButton: 'swal-custom-confirm',
    cancelButton: 'swal-custom-cancel',
    actions: 'swal-custom-actions',
};

/**
 * Assainit le HTML d'une popin. Les messages viennent souvent du Pass (`error.response.data.message`) :
 * on garde la mise en forme (classes, styles, liens) mais pas de script ni de gestionnaire d'événement.
 */
export const sanitizeHtml = (html: string): string => DOMPurify.sanitize(html, { ADD_ATTR: ['target'] });

export type NotifyOptions = SweetAlertOptions & {
    /** Appelée quand l'utilisateur confirme la popin. */
    successFunction?: () => void;
};

/**
 * Popin de retour à l'utilisateur, à la charte de l'espace.
 *
 * Remplace l'action redux `setNotification` : mêmes options SweetAlert2, même `successFunction`
 * déclenchée à la confirmation.
 */
export const notify = ({ successFunction, ...options }: NotifyOptions): Promise<SweetAlertResult> =>
    Swal.fire({
        ...options,
        ...(typeof options.html === 'string' ? { html: sanitizeHtml(options.html) } : {}),
        customClass: swalCustomClass,
        buttonsStyling: false,
    } as SweetAlertOptions).then((result) => {
        if (result.isConfirmed && successFunction) {
            successFunction();
        }
        return result;
    });

/** Popin d'erreur standard : « Attention ! » + message du Pass. */
export const notifyError = (html: string, options: NotifyOptions = {}) =>
    notify({
        icon: 'error',
        title: 'Attention !',
        html,
        confirmButtonText: "J'ai compris",
        ...options,
    });
